import type { FixingIndexMarkMessageData } from '@talos/kyoko';
import {
  EMPTY_ARRAY,
  EMPTY_MAP,
  EMPTY_OBJECT,
  FIXING_INDEX,
  MARKET_FIXING_INDEX,
  TRADABLE_INDEX,
  useObservableValue,
  useStaticSubscription,
  useSubscription,
  wsScanToMap,
  type FixingIndex,
  type MarketFixingIndex,
  type SubscriptionResponse,
  type TradableIndex,
} from '@talos/kyoko';
import { uniq } from 'lodash-es';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { scan, shareReplay } from 'rxjs/operators';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useGetFixingIndexMarksQuery } from './AppStateProvider/streamingDataSlice';

export type IFixingIndexContext = {
  /** Fixing indices list coming from the FixingIndex stream */
  fixingIndicesList: FixingIndex[];
  fixingIndicesByName: Map<string, FixingIndex>;
  fixingIndicesByIndexID: Map<string, FixingIndex>;
  /**
   * A double-map denoting what indices can be selected for what symbols. Its a double-map because we need to support compound instruments.
   * For non-compound instruments, eg BTC-USD, the middle-layer of the mapping is just BTC-USD repeated.
   *
   * Example compound instrument entry (as an object): `{ "FUND:BITE:2026-01-11": { "BTC-USD": ["BRRNY", "XBX"], "ETH-USD": ["ECX"] } }`
   *
   * Example non-compound entry (as an object): `{ "BTC-USD": { "BTC-USD": ["BRRNY", "XBX"] } }`
   */
  tradableIndicesBySymbol: Map<string, Map<string, string[]>>;
  /** Hooked up to the backend MarketIndex stream, returning unique market names by fixing index */
  marketsByFixingIndex: Map<string, string[]>;
  /** Fixing index marks by index ID, used for mapping to fixing index marks */
  fixingIndexMarksByIndexID: Map<string, FixingIndexMarkMessageData>;
  /** Fixing index marks by fixing index name */
  indexMarksByFixingName: Map<string, FixingIndexMarkMessageData>;
  isLoaded: boolean;
};

export const FixingIndexContext = createContext<IFixingIndexContext | undefined>(undefined);

export function useFixingIndices() {
  const context = useContext(FixingIndexContext);
  if (!context) {
    throw new Error('useFixingIndexContext must be used within a FixingIndexProvider');
  }
  return context;
}

export function FixingIndexProvider({ children }: PropsWithChildren) {
  const fixingIndexSubscription = useStaticSubscription<SubscriptionResponse<FixingIndex>>({
    name: FIXING_INDEX,
    tag: 'FixingIndexProvider',
  });
  const marketFixingIndexSubscription = useStaticSubscription<SubscriptionResponse<MarketFixingIndex>>({
    name: MARKET_FIXING_INDEX,
    tag: 'FixingIndexProvider',
  });

  const {
    spreadToFixingSymbolIndexesMap,
    useFundDefinitionAndTradableIndexesStreams,
    enableOTCSupport,
    enableIndexMarkSubs,
  } = useFeatureFlag();

  const { data: fixingIndexMarksData } = useGetFixingIndexMarksQuery(EMPTY_OBJECT, {
    skip: !enableOTCSupport && !enableIndexMarkSubs,
  });

  const tradableIndexSubscription = useSubscription<SubscriptionResponse<TradableIndex>>(
    // Will remove this feature flag after we remove the backward compat support for the flow going thru OrgConfigurationKey.SpreadToFixingSymbolIndexesMap
    useFundDefinitionAndTradableIndexesStreams
      ? {
          name: TRADABLE_INDEX,
          tag: 'FixingIndexProvider',
        }
      : null
  );

  const fixingIndicesByName = useObservableValue(
    () =>
      fixingIndexSubscription.data.pipe(
        wsScanToMap({ getUniqueKey: d => d.Name, newMapEachUpdate: true }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [fixingIndexSubscription.data]
  );

  const fixingIndicesByIndexID = useObservableValue(
    () =>
      fixingIndexSubscription.data.pipe(
        wsScanToMap({ getUniqueKey: d => d.IndexID, newMapEachUpdate: true }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [fixingIndexSubscription.data]
  );

  const marketsByFixingIndex: Map<string, string[]> | undefined = useObservableValue(
    () =>
      marketFixingIndexSubscription.data.pipe(
        scan((acc, json) => {
          if (json.data == null) {
            return acc;
          }
          const map = new Map<string, string[]>(acc);
          if (json.initial) {
            map.clear();
          }
          for (const marketFixingIndex of json.data) {
            const markets = uniq([...(map.get(marketFixingIndex.Index) ?? []), marketFixingIndex.Market]);
            map.set(marketFixingIndex.Index, markets);
          }
          return map;
        }, new Map<string, string[]>()),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [marketFixingIndexSubscription.data]
  );

  const fixingIndexMarksByIndexID = fixingIndexMarksData ?? EMPTY_MAP;

  const indexMarksByFixingName = useMemo(
    () => mapIndexMarksByFixingName(fixingIndicesByName, fixingIndexMarksByIndexID),
    [fixingIndicesByName, fixingIndexMarksByIndexID]
  );

  const fixingIndicesList = useMemo(
    () => (fixingIndicesByName == null ? undefined : [...fixingIndicesByName.values()]),
    [fixingIndicesByName]
  );

  const inputStreamTradableIndicesBySymbol = useObservableValue(
    () =>
      tradableIndexSubscription.data.pipe(
        wsScanToMap({ getUniqueKey: t => t.Symbol, newMapEachUpdate: true }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [tradableIndexSubscription.data]
  );

  const streamTradableIndicesBySymbol: Map<string, Map<string, string[]>> | undefined = useMemo(() => {
    if (fixingIndicesByName == null || inputStreamTradableIndicesBySymbol == null) {
      return undefined;
    }

    const output = new Map<string, Map<string, string[]>>();

    inputStreamTradableIndicesBySymbol.forEach(tradableIndex => {
      output.set(tradableIndex.Symbol, new Map(tradableIndex.Constituents.map(c => [c.Symbol, c.Indexes])));
    });

    return output;
  }, [fixingIndicesByName, inputStreamTradableIndicesBySymbol]);

  const orgConfigTradableIndicesBySymbol: Map<string, Map<string, string[]>> | undefined = useMemo(() => {
    if (spreadToFixingSymbolIndexesMap == null || fixingIndicesByName == null) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(spreadToFixingSymbolIndexesMap) as Record<string, string[]>;
      const output = new Map();
      for (const [symbol, fixingIndexNames] of Object.entries(parsed)) {
        // For the purpose of aligning the new TradableIndex flow with this old org config tradable indices, we add another layer of (useless) mapping here
        output.set(symbol, new Map([[symbol, fixingIndexNames]]));
      }
      return output;
    } catch (e) {
      return EMPTY_MAP;
    }
  }, [spreadToFixingSymbolIndexesMap, fixingIndicesByName]);

  // Decide which of the new or old data set we use here. They have the exact same structure. This will be removed in the future once the need for backward compat is removed
  const tradableIndicesBySymbol: Map<string, Map<string, string[]>> | undefined = useMemo(
    () =>
      useFundDefinitionAndTradableIndexesStreams ? streamTradableIndicesBySymbol : orgConfigTradableIndicesBySymbol,
    [useFundDefinitionAndTradableIndexesStreams, streamTradableIndicesBySymbol, orgConfigTradableIndicesBySymbol]
  );

  const isLoaded = useMemo(
    () => fixingIndicesList != null && fixingIndicesByName != null && marketsByFixingIndex != null,
    [fixingIndicesList, fixingIndicesByName, marketsByFixingIndex]
  );

  const value = useMemo(
    () => ({
      fixingIndicesList: fixingIndicesList ?? EMPTY_ARRAY,
      fixingIndicesByName: fixingIndicesByName ?? EMPTY_MAP,
      fixingIndicesByIndexID: fixingIndicesByIndexID ?? EMPTY_MAP,
      marketsByFixingIndex: marketsByFixingIndex ?? EMPTY_MAP,
      tradableIndicesBySymbol: tradableIndicesBySymbol ?? EMPTY_MAP,
      fixingIndexMarksByIndexID: fixingIndexMarksByIndexID ?? EMPTY_MAP,
      indexMarksByFixingName: indexMarksByFixingName ?? EMPTY_MAP,
      isLoaded,
    }),
    [
      fixingIndicesList,
      fixingIndicesByName,
      fixingIndicesByIndexID,
      marketsByFixingIndex,
      tradableIndicesBySymbol,
      fixingIndexMarksByIndexID,
      indexMarksByFixingName,
      isLoaded,
    ]
  );

  return <FixingIndexContext.Provider value={value}>{children}</FixingIndexContext.Provider>;
}

/** Maps current fixing-index marks from index ID keys to fixing-name keys. */
export function mapIndexMarksByFixingName(
  fixingIndicesByName: Map<string, FixingIndex> | undefined = EMPTY_MAP,
  fixingIndexMarksByIndexID: Map<string, FixingIndexMarkMessageData> | undefined = EMPTY_MAP
): Map<string, FixingIndexMarkMessageData> {
  const output = new Map<string, FixingIndexMarkMessageData>();
  fixingIndicesByName.forEach(fixingIndex => {
    const mark = fixingIndexMarksByIndexID.get(fixingIndex.IndexID);
    if (mark != null) {
      output.set(fixingIndex.Name, mark);
    }
  });
  return output;
}
